import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getCollaborators } from '@/features/Collaborators/Services/collaboratorPrivateService';
import { ALLOCATION_FETCH_CONCURRENCY } from '../exportsPage.definitions';
import type {
    AllocationExportItem,
    AllocationHoursItem,
    AllocationResponsibleMap,
    ExportProgressHandler,
} from '../exportsPage.types';

const COLLABORATORS_COLLECTION = 'collaborators_private';
const ALLOCATIONS_SUBCOLLECTION = 'allocations';

/** Converte Timestamp do Firestore, Date ou string ISO em Date. */
export function toDate(value: unknown): Date | null {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'object' && typeof (value as any).toDate === 'function') {
        const parsed = (value as any).toDate();
        return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
}

/** Executa `task` sobre `items` com no máximo `concurrency` chamadas simultâneas. */
async function mapWithConcurrency<T, R>(
    items: T[],
    concurrency: number,
    task: (item: T) => Promise<R>
): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let cursor = 0;

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            results[index] = await task(items[index]);
        }
    });

    await Promise.all(workers);
    return results;
}

/**
 * Lê a subcoleção de alocações de cada colaborador e devolve tudo achatado.
 *
 * A leitura é feita colaborador a colaborador (com concorrência limitada)
 * porque `firestore.rules` não expõe `allocations` como collection group.
 */
export async function fetchAllocationExportItems(
    onProgress?: ExportProgressHandler
): Promise<AllocationExportItem[]> {
    const collaborators = await getCollaborators({ orderByField: 'name', orderDirection: 'asc' });
    const total = collaborators.length;
    let loaded = 0;

    onProgress?.(0, total);

    const perCollaborator = await mapWithConcurrency(
        collaborators,
        ALLOCATION_FETCH_CONCURRENCY,
        async (collaborator) => {
            const collaboratorId = collaborator.id;
            if (!collaboratorId) {
                loaded += 1;
                onProgress?.(loaded, total);
                return [] as AllocationExportItem[];
            }

            const snapshot = await getDocs(
                collection(db, COLLABORATORS_COLLECTION, collaboratorId, ALLOCATIONS_SUBCOLLECTION)
            );

            loaded += 1;
            onProgress?.(loaded, total);

            return snapshot.docs.map((doc) => {
                const raw = doc.data();
                return {
                    collaboratorId,
                    collaboratorName: collaborator.name ?? null,
                    allocationType: raw.allocationType ?? null,
                    status: raw.status ?? null,
                    clientName: raw.clientName ?? null,
                    projectName: raw.projectName ?? null,
                    startDate: toDate(raw.startDate),
                    endDate: toDate(raw.endDate),
                } satisfies AllocationExportItem;
            });
        }
    );

    return perCollaborator.flat();
}

/**
 * Lê as alocações de cada colaborador só para extrair o Responsável Raro
 * (`responsibleName`) de cada uma.
 *
 * `allocationSummary` não carrega esse campo, então não há como resolvê-lo sem
 * abrir a subcoleção — mesma leitura usada pelas planilhas de alocação.
 */
export async function fetchAllocationResponsibles(
    collaboratorIds: string[],
    onProgress?: ExportProgressHandler
): Promise<AllocationResponsibleMap> {
    const ids = collaboratorIds.filter(Boolean);
    const total = ids.length;
    let loaded = 0;

    onProgress?.(0, total);

    const byCollaborator = new Map<string, Map<string, string>>();

    await mapWithConcurrency(ids, ALLOCATION_FETCH_CONCURRENCY, async (collaboratorId) => {
        const snapshot = await getDocs(
            collection(db, COLLABORATORS_COLLECTION, collaboratorId, ALLOCATIONS_SUBCOLLECTION)
        );

        const responsibles = new Map<string, string>();
        snapshot.docs.forEach((doc) => {
            const responsibleName = (doc.data().responsibleName as string | null | undefined)?.trim();
            if (responsibleName) {
                responsibles.set(doc.id, responsibleName);
            }
        });

        if (responsibles.size > 0) {
            byCollaborator.set(collaboratorId, responsibles);
        }

        loaded += 1;
        onProgress?.(loaded, total);
    });

    return byCollaborator;
}

/**
 * Lê as alocações dos colaboradores informados com o bucket de horas, para
 * medir consumo de capacidade.
 *
 * Diferente de `fetchAllocationExportItems`, a leitura é restrita aos ids
 * recebidos — a planilha de horas só olha desenvolvedores, então não há motivo
 * para varrer a base inteira.
 */
export async function fetchAllocationHoursItems(
    collaboratorIds: string[],
    onProgress?: ExportProgressHandler
): Promise<AllocationHoursItem[]> {
    const ids = collaboratorIds.filter(Boolean);
    const total = ids.length;
    let loaded = 0;

    onProgress?.(0, total);

    const perCollaborator = await mapWithConcurrency(
        ids,
        ALLOCATION_FETCH_CONCURRENCY,
        async (collaboratorId) => {
            const snapshot = await getDocs(
                collection(db, COLLABORATORS_COLLECTION, collaboratorId, ALLOCATIONS_SUBCOLLECTION)
            );

            loaded += 1;
            onProgress?.(loaded, total);

            return snapshot.docs.map((doc) => {
                const raw = doc.data();
                return {
                    collaboratorId,
                    allocationType: raw.allocationType ?? null,
                    status: raw.status ?? null,
                    projectName: raw.projectName ?? null,
                    bucket: typeof raw.bucket === 'number' ? raw.bucket : null,
                    bucketType: raw.bucketType ?? null,
                    startDate: toDate(raw.startDate),
                    endDate: toDate(raw.endDate),
                } satisfies AllocationHoursItem;
            });
        }
    );

    return perCollaborator.flat();
}
